import { useLayoutEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useUIStore } from '@/stores/uiStore'
import { useSettings } from '@/hooks/useSettings'
import type { Collection } from '@/types'

interface HorizontalShowcaseProps {
  title: string
  subtitle?: string
  collections: Collection[]
}

export function HorizontalShowcase({ title, subtitle, collections }: HorizontalShowcaseProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const prevProgress = useRef(0)
  const { setNavHidden, setNavTheme } = useUIStore()
  const { storefront } = useSettings()
  const featuredQuote = storefront?.featuredQuote ?? 'Fragrance is the most intense form of memory.'

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  })

  // The horizontal distance the track must travel is (trackWidth - viewportWidth).
  // We scale that by a scroll factor so the user has a comfortable amount of
  // vertical scroll to move through the cards, then we add room for the quote
  // panel to slide in and expand. Previously the height was hard-coded to
  // 500vh per collection, which made the scroll feel compressed and the quote
  // animation rush to completion.
  const [targetX, setTargetX] = useState(0)
  const [sectionHeight, setSectionHeight] = useState('400vh')
  useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current
      const sticky = track?.parentElement
      if (!track || !sticky) return
      const trackWidth = track.scrollWidth
      const viewWidth = sticky.clientWidth
      // Translate until the last card's right edge has cleared the viewport.
      // A small extra buffer (5vw) guarantees nothing peeks back in.
      const buffer = viewWidth * 0.05
      const travel = trackWidth - viewWidth + buffer
      setTargetX(-travel)

      // Section height = viewport + travel * scrollFactor + quote room.
      // 2.5x travel gives a smooth horizontal scroll pace; 120vh extra lets the
      // quote slide in and expand without racing to the end of the section.
      const scrollFactor = 2.5
      const quoteRoom = viewWidth * 1.2
      const heightPx = sticky.clientHeight + travel * scrollFactor + quoteRoom
      setSectionHeight(`${heightPx}px`)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [collections.length])

  // The card track finishes its horizontal motion before the quote appears.
  // We reserve 0-0.55 for the cards, 0.55-1.0 for the quote to slide in and
  // expand, so the third (or last) card is fully off-screen before the quote
  // overlaps.
  const trackEnd = 0.55
  const x = useTransform(scrollYProgress, [0, trackEnd], [0, targetX])

  // Quote overlay: slides in from the right AFTER the cards have cleared,
  // starts at card size, then expands to fill the viewport.
  //
  // Timeline (scrollYProgress):
  //   0.00 - 0.55  : cards scroll horizontally and fully clear
  //   0.55 - 0.75  : quote slides in from right and fades in
  //   0.75 - 0.95  : quote expands from card size to full viewport
  //   0.95 - 1.00  : quote fully fills the viewport, ready to become the next section
  const quoteSlideStart = 0.55
  const quoteSlideEnd = 0.75
  const quoteExpandStart = 0.75
  const quoteExpandEnd = 0.95

  const quoteX = useTransform(
    scrollYProgress,
    [quoteSlideStart, quoteSlideEnd],
    ['100%', '0%']
  )
  const quoteOpacity = useTransform(
    scrollYProgress,
    [quoteSlideStart, quoteSlideStart + 0.12],
    [0, 1]
  )
  const quoteWidth = useTransform(
    scrollYProgress,
    [quoteExpandStart, quoteExpandEnd],
    ['60vw', '100vw']
  )
  const quoteHeight = useTransform(
    scrollYProgress,
    [quoteExpandStart, quoteExpandEnd],
    ['45vw', '100vh']
  )

  // Collections title fades once the quote panel starts taking over.
  const titleOpacity = useTransform(
    scrollYProgress,
    [quoteSlideStart - 0.05, quoteSlideStart + 0.05],
    [1, 0]
  )

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const inside = latest > 0.01 && latest < 0.99
    if (inside) {
      // Switch to light nav once the quote (mushroom background) begins dominating.
      setNavTheme(latest > quoteExpandStart + 0.1 ? 'default' : 'dark')
      setNavHidden(latest > prevProgress.current)
    } else {
      setNavTheme('default')
      setNavHidden(false)
    }
    prevProgress.current = latest
  })

  return (
    <section
      ref={containerRef}
      className="relative bg-wd-deep text-white"
      style={{ height: sectionHeight }}
    >
      {/** Sticky viewport: vertical overflow is hidden, but horizontal overflow is
        * allowed so the track keeps its full content width for the px transform.
        * The root wrapper clips any horizontal page overflow. */}
      <div className="sticky top-0 h-screen overflow-y-hidden">
        {/* Section header pinned at the top of the viewport */}
        <motion.div
          style={{ opacity: titleOpacity }}
          className="absolute left-6 top-16 z-20 lg:left-10"
        >
          <p className="mb-3 font-condensed text-xs uppercase tracking-[0.2em] text-white">
            {subtitle}
          </p>
          <h2 className="font-display text-display-lg lg:text-display-xl font-normal normal-case leading-[0.95]">
            {title}
          </h2>
        </motion.div>

        {/* Horizontal card track */}
        <motion.div
          ref={trackRef}
          style={{ x }}
          className="flex h-full items-center gap-6 px-6 pt-32 will-change-transform lg:px-10"
        >
          {collections.map((collection) => (
            <Link
              key={collection.id}
              to={`/collections/${collection.slug}`}
              className="group relative aspect-[4/3] w-[60vw] max-w-[620px] flex-shrink-0 overflow-hidden"
            >
              <img
                src={collection.image}
                alt={collection.name}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-wd-deep/90 via-wd-deep/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-between p-6 lg:p-8">
                <p className="font-condensed text-xs uppercase tracking-[0.2em] text-white/70">
                  {collection.tagline}
                </p>
                <div>
                  <h3 className="font-display text-3xl normal-case text-white lg:text-4xl">
                    {collection.name.toLowerCase()}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-white/70">{collection.description}</p>
                </div>
              </div>
            </Link>
          ))}

          {/* Breathing room so the last card fully clears the viewport */}
          <div className="w-[35vw] shrink-0" />
        </motion.div>

        {/* Quote section slides in from the right and expands to fill the viewport.
            It starts at the same size as a collection card and vertically centered. */}
        <motion.div
          style={{
            x: quoteX,
            width: quoteWidth,
            height: quoteHeight,
            opacity: quoteOpacity,
            y: '-50%',
          }}
          className="absolute right-0 top-1/2 z-10 flex items-center justify-center bg-card px-6 text-carbon lg:px-10"
        >
          <div className="grid-lines text-carbon absolute inset-0">
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
          <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
            <p className="font-display text-display-md lg:text-display-lg italic leading-tight text-carbon">
              “{featuredQuote}”
            </p>
            <p className="mt-8 font-condensed text-xs uppercase tracking-[0.2em] text-carbon/60">
              — Little Sky
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
